import { Plus, IdCard, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export const DriversTab = ({
  drivers,
  showAddDriver, setShowAddDriver,
  newDriver, setNewDriver,
  editingDriver, setEditingDriver,
  saveDriver, editDriver, deleteDriver,
  copyDriverLink, regenerateDriverToken,
}) => {
  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-emerald-400 flex items-center gap-2">
              <IdCard className="w-5 h-5" /> Chauffeurs
            </h3>
            <p className="text-zinc-400 text-xs mt-1">
              Ajoutez les chauffeurs partenaires. Vous pourrez ensuite les assigner à une course — leur nom et immatriculation apparaîtront sur le bon de réservation (police).
            </p>
          </div>
          <Button
            onClick={() => { setEditingDriver(null); setNewDriver({ first_name: '', last_name: '', license_plate: '', phone: '', notes: '', commission_percent: 70, company_name: '', company_legal_form: '', company_siren: '', company_siret: '', company_ape: '', company_address: '', company_vat: '', evtc_card_number: '' }); setShowAddDriver(true); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
            data-testid="add-driver-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>

        {showAddDriver && (
          <div className="mb-4 p-4 bg-zinc-900/70 rounded-lg border border-emerald-500/30 space-y-3" data-testid="driver-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-400 text-xs block mb-1">Prénom *</label>
                <Input value={newDriver.first_name} onChange={e => setNewDriver({...newDriver, first_name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="driver-firstname" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs block mb-1">Nom *</label>
                <Input value={newDriver.last_name} onChange={e => setNewDriver({...newDriver, last_name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="driver-lastname" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs block mb-1">Immatriculation *</label>
                <Input value={newDriver.license_plate} onChange={e => setNewDriver({...newDriver, license_plate: e.target.value.toUpperCase()})} placeholder="AB-123-CD" className="bg-zinc-800 border-zinc-700 text-white text-sm uppercase" data-testid="driver-plate" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs block mb-1">Téléphone</label>
                <Input value={newDriver.phone} onChange={e => setNewDriver({...newDriver, phone: e.target.value})} placeholder="06..." className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="driver-phone" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs block mb-1">Commission (%) — prix net chauffeur auto-calculé</label>
                <Input
                  type="number" min="0" max="100" step="1"
                  value={newDriver.commission_percent}
                  onChange={e => setNewDriver({...newDriver, commission_percent: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  data-testid="driver-commission"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-xs block mb-1">Notes</label>
                <Input value={newDriver.notes} onChange={e => setNewDriver({...newDriver, notes: e.target.value})} placeholder="Marque/modèle véhicule, carte VTC, etc." className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="driver-notes" />
              </div>
            </div>

            {/* Société exécutante (entreprise du chauffeur) */}
            <details className="mt-2 bg-zinc-950/50 border border-amber-500/30 rounded-lg" data-testid="driver-company-section">
              <summary className="cursor-pointer px-4 py-2 text-amber-300 text-sm font-semibold flex items-center gap-2">
                🏢 Société exécutante (SIRET, adresse...) — facultatif mais recommandé pour le bon de réservation
              </summary>
              <div className="p-4 pt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="text-amber-300 text-xs block mb-1">Nom de l'entreprise</label>
                  <Input value={newDriver.company_name || ''} onChange={e => setNewDriver({...newDriver, company_name: e.target.value})} placeholder="Ex: BETELGUERIEV (Entrepreneur individuel)" className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm" data-testid="driver-company-name" />
                </div>
                <div>
                  <label className="text-amber-300 text-xs block mb-1">Forme juridique</label>
                  <Input value={newDriver.company_legal_form || ''} onChange={e => setNewDriver({...newDriver, company_legal_form: e.target.value})} placeholder="Ex: Entrepreneur individuel" className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm" data-testid="driver-company-legal" />
                </div>
                <div>
                  <label className="text-amber-300 text-xs block mb-1">Code APE</label>
                  <Input value={newDriver.company_ape || ''} onChange={e => setNewDriver({...newDriver, company_ape: e.target.value})} placeholder="Ex: 49.32Z" className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm font-mono" data-testid="driver-company-ape" />
                </div>
                <div>
                  <label className="text-amber-300 text-xs block mb-1">SIREN (9 chiffres)</label>
                  <Input value={newDriver.company_siren || ''} onChange={e => setNewDriver({...newDriver, company_siren: e.target.value})} placeholder="Ex: 909 405 326" className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm font-mono" data-testid="driver-company-siren" />
                </div>
                <div>
                  <label className="text-amber-300 text-xs block mb-1">SIRET (14 chiffres)</label>
                  <Input value={newDriver.company_siret || ''} onChange={e => setNewDriver({...newDriver, company_siret: e.target.value})} placeholder="Ex: 909 405 326 00010" className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm font-mono" data-testid="driver-company-siret" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-amber-300 text-xs block mb-1">Adresse du siège</label>
                  <textarea
                    value={newDriver.company_address || ''}
                    onChange={e => setNewDriver({...newDriver, company_address: e.target.value})}
                    placeholder="Ex: LES HEURES CLAIRES 2 ENTREE B, 4 RUE DES COTEAUX, 06300 NICE"
                    rows={2}
                    className="w-full px-3 py-2 bg-zinc-900 border border-amber-500/40 rounded-lg text-amber-100 text-sm resize-none"
                    data-testid="driver-company-address"
                  />
                </div>
                <div>
                  <label className="text-amber-300 text-xs block mb-1">N° TVA Intra (optionnel)</label>
                  <Input value={newDriver.company_vat || ''} onChange={e => setNewDriver({...newDriver, company_vat: e.target.value})} placeholder="Ex: FR12345678901" className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm font-mono" data-testid="driver-company-vat" />
                </div>
                <div>
                  <label className="text-amber-300 text-xs block mb-1">N° carte pro VTC</label>
                  <Input value={newDriver.evtc_card_number || ''} onChange={e => setNewDriver({...newDriver, evtc_card_number: e.target.value})} placeholder="Ex: 06-2022-09-12345" className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm font-mono" data-testid="driver-evtc" />
                </div>
              </div>
            </details>
            <div className="flex gap-2">
              <Button
                onClick={saveDriver}
                disabled={!newDriver.first_name || !newDriver.last_name || !newDriver.license_plate}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                data-testid="save-driver-btn"
              >
                {editingDriver ? 'Mettre à jour' : 'Enregistrer'}
              </Button>
              <Button variant="outline" onClick={() => { setShowAddDriver(false); setEditingDriver(null); }} className="border-zinc-700 text-zinc-400 text-sm">
                Annuler
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2" data-testid="drivers-list">
          {drivers.length === 0 ? (
            <p className="text-zinc-500 text-sm italic">Aucun chauffeur enregistré.</p>
          ) : drivers.map(d => (
            <div key={d.id} className="p-3 bg-zinc-800/60 rounded-lg border border-zinc-700 hover:border-emerald-500/40 transition" data-testid={`driver-row-${d.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm">
                    {d.first_name} {d.last_name}
                    <span className="ml-2 inline-block px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-emerald-300 text-xs font-mono">
                      {d.license_plate}
                    </span>
                    {typeof d.commission_percent === 'number' && (
                      <span className="ml-2 inline-block px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded text-amber-300 text-xs">
                        {d.commission_percent}%
                      </span>
                    )}
                  </p>
                  <p className="text-zinc-400 text-xs mt-0.5">
                    {d.phone || '—'} {d.notes ? `• ${d.notes}` : ''}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => editDriver(d)} className="border-zinc-700 text-zinc-300 text-xs h-7" data-testid={`edit-driver-${d.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteDriver(d.id, `${d.first_name} ${d.last_name}`)} className="border-red-500/50 text-red-400 text-xs h-7" data-testid={`delete-driver-${d.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {d.access_token && (
                <div className="mt-2 flex flex-wrap items-center gap-2 pt-2 border-t border-zinc-700/50">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Lien portail chauffeur :</span>
                  <code className="text-[10px] text-emerald-300 font-mono bg-zinc-900 px-2 py-0.5 rounded max-w-[200px] truncate" title={`/chauffeur/${d.access_token}`}>
                    /chauffeur/{d.access_token.slice(0, 12)}…
                  </code>
                  <button
                    onClick={() => copyDriverLink(d)}
                    className="text-[10px] text-emerald-400 hover:text-emerald-300 underline"
                    data-testid={`copy-driver-link-${d.id}`}
                  >
                    Copier
                  </button>
                  <button
                    onClick={() => regenerateDriverToken(d.id)}
                    className="text-[10px] text-amber-400 hover:text-amber-300 underline"
                    data-testid={`regen-driver-token-${d.id}`}
                  >
                    Régénérer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
